/**
 * Sidebar panel. Renders the left builder rail (width from --pb-panel-width-left)
 * with navigation tabs (Sections, Design, Project) and switchable panes.
 * Provides mount points for SectionList, PropertyPanel theme, and ClientSeoPanel.
 *
 * Open/close and Escape key handling are owned by shell.js via .pb-collapsed.
 * This module only manages tab switching and emits sidebar:ensure-open
 * when automation needs the panel visible.
 */

import { SIDEBAR_TAB_LABELS } from '../shared/ui-vocabulary.js';
import { eventBus } from '../core/events.js';
import { PanelTopZone } from './panel-top-zone.js';
import { PanelBottomZone } from './panel-bottom-zone.js';
import { ShipPanel } from './ship-panel.js';

// User-visible labels. The `id` is internal — `client-seo` is preserved so that
// existing `props:request-tab` routing and pane IDs do not have to change.
const TABS = [
  { id: 'sections', label: SIDEBAR_TAB_LABELS.sections, ppTab: null },
  { id: 'design', label: SIDEBAR_TAB_LABELS.design, ppTab: 'theme' },
  { id: 'client-seo', label: SIDEBAR_TAB_LABELS.project, ppTab: 'client-seo' },
  // Label only: the internal id `audit` routes props:request-tab, the pane
  // element id and panel:tab-changed. Same move `client-seo` -> "Project" made.
  { id: 'audit', label: SIDEBAR_TAB_LABELS.audit, ppTab: 'audit' }
];

export class NavSidebar {
  /**
   * @param {HTMLElement} container — the #pb-palette (.pb-panel-left) element
   */
  constructor(container) {
    this._container = container;
    this._activeTab = 'sections';
    this._listeners = [];
    this._render();
    this._wireEvents();
  }

  // ── Render ──

  _render() {
    this._container.innerHTML = '';

    const root = document.createElement('div');
    root.className = 'pb-sidebar';

    // Top zone owns the tab strip + search + breadcrumb.
    this._topZone = new PanelTopZone({
      container: root,
      tabs: TABS.map((t) => ({ internalId: t.id, label: t.label })),
      activeTabId: this._activeTab,
      onTabClick: (id) => this._showTab(id)
    });

    // Body with panes.
    const body = document.createElement('div');
    body.className = 'pb-sidebar-body';

    this._panes = {};
    for (const tab of TABS) {
      const pane = document.createElement('div');
      pane.setAttribute('role', 'tabpanel');
      pane.id = `pb-sidebar-pane-${tab.id}`;
      pane.className = 'pb-sidebar-pane' + (tab.id === 'sections' ? ' pb-sidebar-pane-active' : '');
      pane.setAttribute('aria-labelledby', `pb-sidebar-tab-${tab.id}`);
      this._panes[tab.id] = pane;
      body.appendChild(pane);
    }

    // Design pane has two mutually-exclusive containers: theme (default) and section.
    this._themeContainer = document.createElement('div');
    this._themeContainer.className = 'pb-props-body';
    this._sectionContainer = document.createElement('div');
    this._sectionContainer.className = 'pb-props-body';
    this._sectionContainer.hidden = true;
    this._sectionContainer.setAttribute('aria-hidden', 'true');
    this._panes.design.appendChild(this._themeContainer);
    this._panes.design.appendChild(this._sectionContainer);
    this._auditTab = new ShipPanel(this._panes.audit);

    root.appendChild(body);

    // Bottom zone (clipboard tray + future ambient actions).
    const bottomMount = document.createElement('div');
    this._bottomZone = new PanelBottomZone({ container: bottomMount });
    root.appendChild(bottomMount);

    this._container.appendChild(root);
  }

  // ── Event wiring ──

  _wireEvents() {
    // Keep compatibility with existing "open properties/theme tab" requests.
    this._listen('props:request-tab', (payload = {}) => {
      const tab = payload.tab;
      if (tab === 'properties') {
        this._showTab('design', { emitTabEvent: false });
        eventBus.emit('sidebar:tab-changed', { tab: 'section' });
      } else if (tab === 'theme') {
        this._showTab('design', { emitTabEvent: false });
        eventBus.emit('sidebar:tab-changed', { tab: 'theme' });
      } else if (tab === 'client-seo' || tab === 'client' || tab === 'seo') {
        this._showTab('client-seo', { emitTabEvent: false });
      } else if (tab === 'audit') {
        this._showTab('audit', { emitTabEvent: false });
      } else if (tab === 'sections') {
        this._showTab('sections', { emitTabEvent: false });
      } else return;

      // `reveal: false` switches the tab WITHOUT forcing the rail open.
      //
      // Every caller used to reveal, which made a canvas selection re-open a rail
      // the user had deliberately collapsed — and then paint it over the element
      // they had just selected. For a nav logo, which lives at the top-left
      // exactly where this rail sits, that produced a loop with no way out:
      // collapse to reach the logo, click it, and the rail springs back on top.
      //
      // Revealing stays the DEFAULT because the deliberate callers — the Publish checks
      // panel, the audit actions, the tour, a section-list click — are asking to
      // be looked at. Only selection-driven routing opts out.
      if (payload.reveal !== false) eventBus.emit('sidebar:ensure-open');
    });
  }

  _listen(event, fn) {
    eventBus.on(event, fn);
    this._listeners.push([event, fn]);
  }

  // ── Tab switching ──

  _showTab(id, options = {}) {
    const { emitTabEvent = true } = options;
    if (this._activeTab === id) return;
    this._activeTab = id;

    this._topZone?.setActiveTab(id);

    // Update panes
    for (const [paneId, pane] of Object.entries(this._panes)) {
      pane.classList.toggle('pb-sidebar-pane-active', paneId === id);
    }

    // Notify PropertyPanel of tab context (legacy event — keyed off ppTab)
    const tabDef = TABS.find((t) => t.id === id);
    if (emitTabEvent && tabDef?.ppTab) {
      eventBus.emit('sidebar:tab-changed', { tab: tabDef.ppTab });
    }

    // Unified event keyed off the panel tab id directly (sections / design /
    // client-seo). Subscribed by panel-top-zone and other
    // chrome that needs the panel-level tab regardless of PropertyPanel's
    // internal ppTab nomenclature.
    eventBus.emit('panel:tab-changed', { tab: id });
  }

  // ── Public API ──

  get sectionsContainer() {
    return this._panes.sections;
  }
  get themeContainer() {
    return this._themeContainer;
  }
  get sectionContainer() {
    return this._sectionContainer;
  }
  get clientSeoContainer() {
    return this._panes['client-seo'];
  }
  get auditContainer() {
    return this._panes.audit;
  }

  switchTab(tabName) {
    if (tabName === 'theme') this._showTab('design');
    else if (tabName === 'client-seo' || tabName === 'client' || tabName === 'seo')
      this._showTab('client-seo');
    else if (tabName === 'audit') this._showTab('audit');
    else if (tabName === 'sections') this._showTab('sections');
  }

  destroy() {
    if (this._topZone) {
      this._topZone.destroy();
      this._topZone = null;
    }
    if (this._bottomZone) {
      this._bottomZone.destroy();
      this._bottomZone = null;
    }
    if (this._auditTab) {
      this._auditTab.destroy();
      this._auditTab = null;
    }
    for (const [event, fn] of this._listeners) {
      eventBus.off(event, fn);
    }
    this._listeners = [];
  }
}
